import Phaser from 'phaser';
import { suitLine } from '../../content/story';
import { SEEN, needsTutorial } from '../../meta/progress';
import { TRACKS, buyUpgrade, canBuy, nextCost, type Track } from '../../meta/upgrades';
import { Sfx } from '../audio/Sfx';
import { paintSheet } from '../render/art';
import { LAB_PAD, paintLab } from '../render/labArt';
import { PLAYER_FRAME, PLAYER_SHEET, SHARD_SHEET, type SpriteSheet } from '../render/sprites';
import { newSeed, type RunState } from '../run';
import { saveStore } from '../storage';
import { CELL_H, CELL_W, PIXEL_FONT } from '../ui/pixelFont';
import { wrap } from '../ui/wrap';

const PANEL_W = 236;
const ROW_H = 30;
const ROWS_TOP = 50;
/** Where the suit's words start, under the window. */
const SPEECH_Y = 110;

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

const inside = (b: Box, x: number, y: number) =>
  x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;

/**
 * Dad's lab, the hub between runs (GDD: Core loop). The suit talks (a new line after every
 * rewind), materials buy permanent upgrades, and LAUNCH starts a run with its transit. Tap a track
 * to select it, then BUY; on a keyboard, arrows select, Enter buys, Space launches.
 */
export class LabScene extends Phaser.Scene {
  run!: RunState;
  private sfx!: Sfx;
  private gfx!: Phaser.GameObjects.Graphics;
  /** Text and icons drawn by the last draw(), cleared on the next. */
  private parts: Phaser.GameObjects.GameObject[] = [];
  private selected = 0;
  private launching = false;

  constructor() {
    super('Lab');
  }

  create(data: Partial<RunState>): void {
    const params = new URLSearchParams(window.location.search);
    this.run = {
      seed: data.seed ?? params.get('seed') ?? newSeed(),
      planet: 0,
      room: 0,
      rewinds: data.rewinds ?? 0,
    };
    this.launching = false;
    this.selected = 0;
    this.sfx = new Sfx(this);
    this.sfx.stopAmbience();

    const w = this.scale.width;
    const h = this.scale.height;
    this.paintTextures(w, h);
    this.add.image(0, 0, 'lab').setOrigin(0);
    const suit = this.add
      .sprite(LAB_PAD.x, h - LAB_PAD.floorFromBottom, 'lab-suit', PLAYER_FRAME.stand)
      .setOrigin(0.5, 1)
      .setScale(2);
    this.tweens.add({ targets: suit, y: suit.y - 1, duration: 900, yoyo: true, repeat: -1 });

    const save = saveStore().save;
    const line = suitLine(save.stats.rewinds, save.seen.includes(SEEN.rangerLog));
    const maxChars = Math.floor((this.panelX() - 30) / CELL_W);
    this.add
      .bitmapText(20, SPEECH_Y, PIXEL_FONT, wrap(line, maxChars).join('\n'), CELL_W)
      .setTint(0x9fd8ff);
    this.add
      .bitmapText(20, SPEECH_Y - CELL_H - 2, PIXEL_FONT, 'THE SUIT:', CELL_W)
      .setTint(0x46557e);

    this.gfx = this.add.graphics();
    this.draw();

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => this.tap(p.x, p.y));
    const kb = this.input.keyboard;
    // Fresh presses only: a key still held from the run (Space for the jetpack through a
    // rewind) repeats, and must not launch at once or buy over and over.
    const press = (key: string, act: () => unknown) =>
      kb?.on(`keydown-${key}`, (e: KeyboardEvent) => {
        if (!e.repeat) act();
      });
    press('UP', () => this.select(this.selected - 1));
    press('W', () => this.select(this.selected - 1));
    press('DOWN', () => this.select(this.selected + 1));
    press('S', () => this.select(this.selected + 1));
    press('ENTER', () => this.buy());
    press('SPACE', () => this.launch());

    // Everything is laid out for the screen's width: lay it out again after a rotation.
    const onResize = () => {
      if (!this.launching) this.scene.restart({ seed: this.run.seed, rewinds: this.run.rewinds });
    };
    this.scale.on('resize', onResize);
    this.events.once('shutdown', () => this.scale.off('resize', onResize));
    this.cameras.main.fadeIn(300, 13, 10, 31);
  }

  /** Selects a track (wrapping round). */
  select(index: number): void {
    const n = TRACKS.length;
    this.selected = ((index % n) + n) % n;
    this.sfx.ui('select');
    this.draw();
  }

  /** Buys the next level of the selected track, if it can. */
  buy(): boolean {
    const track = TRACKS[this.selected];
    if (!track) return false;
    let bought = false;
    saveStore().update((s) => {
      bought = buyUpgrade(s, track.id);
    });
    this.sfx.ui(bought ? 'buy' : 'deny');
    this.draw();
    return bought;
  }

  /** Starts the run: off to the first transit, or the Earth tutorial the very first time. */
  launch(): void {
    if (this.launching) return;
    this.launching = true;
    const tutorial = needsTutorial(saveStore().save);
    saveStore().update((s) => {
      s.stats.runs += 1;
    });
    this.sfx.ui('launch');
    this.cameras.main.fadeOut(400, 7, 5, 26);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      const run: RunState = { ...this.run, tutorial };
      this.scene.start(tutorial ? 'Play' : 'Transit', run);
    });
  }

  /** Where the buttons are, in game pixels (for tools/e2e to tap them). */
  layout(): { rows: Box[]; buy: Box; launch: Box } {
    return {
      rows: TRACKS.map((_, i) => this.rowBox(i)),
      buy: this.buyBox(),
      launch: this.launchBox(),
    };
  }

  private panelX(): number {
    return this.scale.width - PANEL_W - 10;
  }

  private rowBox(i: number): Box {
    return { x: this.panelX() + 6, y: ROWS_TOP + i * ROW_H - 4, w: PANEL_W - 12, h: ROW_H - 3 };
  }

  private buyBox(): Box {
    return {
      x: this.panelX() + 6,
      y: ROWS_TOP + TRACKS.length * ROW_H + 18,
      w: PANEL_W - 12,
      h: 26,
    };
  }

  private launchBox(): Box {
    const h = this.scale.height;
    return { x: this.panelX() - 112, y: h - 54, w: 96, h: 32 };
  }

  private tap(x: number, y: number): void {
    if (inside(this.launchBox(), x, y)) return this.launch();
    if (inside(this.buyBox(), x, y)) {
      this.buy();
      return;
    }
    const row = TRACKS.findIndex((_, i) => inside(this.rowBox(i), x, y));
    if (row >= 0) this.select(row);
  }

  private label(x: number, y: number, text: string, tint = 0xffffff): void {
    this.parts.push(this.add.bitmapText(x, y, PIXEL_FONT, text, CELL_W).setTint(tint));
  }

  private shardIcon(x: number, y: number): void {
    this.parts.push(this.add.image(x, y, 'lab-shard', 0));
  }

  /** Redraws the panel and buttons (only after a change: it is a menu). */
  private draw(): void {
    for (const part of this.parts) part.destroy();
    this.parts = [];
    const g = this.gfx.clear();
    const save = saveStore().save;
    const px = this.panelX();
    const h = this.scale.height;

    g.fillStyle(0x0b0820, 0.86).fillRect(px, 8, PANEL_W, h - 16);
    g.lineStyle(1, 0x46557e, 1).strokeRect(px + 0.5, 8.5, PANEL_W - 1, h - 17);
    this.label(px + 10, 16, 'SUIT UPGRADES', 0xffd23f);
    this.shardIcon(px + 14, 36);
    this.label(px + 24, 32, `MATERIALS ${save.materials}`, 0xe8c8ff);

    TRACKS.forEach((track, i) => this.drawRow(track, i, save.upgrades[track.id], save.materials));

    const track = TRACKS[this.selected];
    if (!track) return;
    const level = save.upgrades[track.id];
    const check = canBuy(track, level, save.materials);
    const cost = nextCost(track, level);
    const effectY = ROWS_TOP + TRACKS.length * ROW_H + 2;
    this.label(px + 10, effectY, track.effect, 0x9fd8ff);

    const buy = this.buyBox();
    const ok = check === 'ok';
    g.fillStyle(ok ? 0x2f7f86 : 0x1d2540, 1).fillRect(buy.x, buy.y, buy.w, buy.h);
    g.lineStyle(1, ok ? 0x7df9ff : 0x46557e, 1).strokeRect(
      buy.x + 0.5,
      buy.y + 0.5,
      buy.w - 1,
      buy.h - 1,
    );
    const buyText =
      check === 'ok'
        ? `BUY FOR ${cost ?? 0}`
        : check === 'short'
          ? `NEED ${(cost ?? 0) - save.materials} MORE`
          : 'FULLY UPGRADED';
    this.label(
      buy.x + Math.round((buy.w - buyText.length * CELL_W) / 2),
      buy.y + 8,
      buyText,
      ok ? 0xffffff : 0x6f76a8,
    );

    const launch = this.launchBox();
    g.fillStyle(0xc45a14, 1).fillRect(launch.x, launch.y, launch.w, launch.h);
    g.fillStyle(0xff8a24, 1).fillRect(launch.x, launch.y, launch.w, 3);
    g.lineStyle(1, 0xffd23f, 1).strokeRect(
      launch.x + 0.5,
      launch.y + 0.5,
      launch.w - 1,
      launch.h - 1,
    );
    this.label(launch.x + Math.round((launch.w - 6 * CELL_W) / 2), launch.y + 11, 'LAUNCH');
  }

  private drawRow(track: Track, i: number, level: number, materials: number): void {
    const g = this.gfx;
    const box = this.rowBox(i);
    if (i === this.selected) {
      g.fillStyle(0x1d2540, 1).fillRect(box.x, box.y, box.w, box.h);
      g.lineStyle(1, 0x7df9ff, 1).strokeRect(box.x + 0.5, box.y + 0.5, box.w - 1, box.h - 1);
    }
    const y = ROWS_TOP + i * ROW_H;
    this.label(box.x + 6, y, track.name);
    for (let k = 0; k < track.levels; k++) {
      const filled = k < level;
      g.fillStyle(filled ? 0x7df9ff : 0x2a2f45, 1).fillRect(box.x + 6 + k * 9, y + 13, 7, 6);
    }
    const check = canBuy(track, level, materials);
    const cost = nextCost(track, level);
    const priced = check === 'ok' || check === 'short';
    const right = check === 'maxed' ? 'MAX' : `${cost ?? ''}`;
    const tint = check === 'ok' ? 0xe8c8ff : check === 'short' ? 0xff6a5a : 0x6f76a8;
    const rightX = box.x + box.w - 8 - right.length * CELL_W - (priced ? 12 : 0);
    this.label(rightX, y + 6, right, tint);
    if (priced) this.shardIcon(box.x + box.w - 12, y + 10);
  }

  private paintTextures(w: number, h: number): void {
    const canvas = (
      key: string,
      cw: number,
      ch: number,
      paint: (c: CanvasRenderingContext2D) => void,
    ) => {
      if (this.textures.exists(key)) this.textures.remove(key);
      const tex = this.textures.createCanvas(key, cw, ch);
      if (!tex) throw new Error(`Could not create texture ${key}`);
      paint(tex.context);
      tex.refresh();
      return tex;
    };
    const sheet = (key: string, s: SpriteSheet) => {
      const tex = canvas(key, s.width * s.frames.length, s.height, (c) => paintSheet(c, s));
      s.frames.forEach((_, i) => tex.add(i, 0, i * s.width, 0, s.width, s.height));
    };
    canvas('lab', w, h, (c) => paintLab(c, w, h));
    sheet('lab-suit', PLAYER_SHEET);
    sheet('lab-shard', SHARD_SHEET);
  }
}
